/** The spend-based multiplier sets - **gate 20, ADR-0044**. Provenance only, and read-only.
 *
 * The library used to say, in one sentence, that these sets "are held separately and are not listed
 * here". That was true and left a reader with no way to see who publishes the multipliers a
 * purchase-based Scope 3 line is priced with. This panel lists each set the way the per-activity
 * library lists its own: publisher, edition, retrieval date, source, file hash, licence and lock -
 * plus the five things a spend set has that an activity set does not: the currency, the **price
 * basis** (current prices or a fixed year), the classification scheme, the VAT treatment and the
 * publisher's own sentence about CO2e.
 *
 * ## What it does not do
 *
 * - **No multiplier is shown.** A set holds one value per classification code per year (and per
 *   variant), which is a catalogue, and a row-level view belongs to the page that prices a purchase.
 *   The count and the span of years are printed so the omission is stated and not silent.
 * - **No horizon is invented.** `gwp_horizon_years` is null where the publisher states none, and
 *   that is printed as *not stated*, never as 100: it is not the same fact, and the model says so at
 *   length. The publisher's own sentence is printed verbatim beside it.
 * - **No control that edits or chooses.** A loaded panel has no button, and a failed one has exactly
 *   one - *Try again*. Choosing between a current-price and a fixed-year series is a purchase
 *   line's recorded decision with a reason (ADR-0044 part 4), not a preference set from here.
 * - **The caveat is not softened.** It is the publisher's, it is about the use this product puts
 *   the set to, and it travels with the set.
 *
 * It fails on its own: the per-activity library above it does not depend on this read, and this
 * panel does not depend on that one.
 */

import type { ReactNode } from "react";

import type { SpendFactorSet } from "../api/spendFactors";
import { useSpendFactorSets } from "../api/spendFactors";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { quantity, shortDate } from "../lib/format";
import own from "./FactorLibrary.module.css";
import styles from "./Screen.module.css";

function Absent({ children }: { children: ReactNode }) {
  return (
    <span className={own.none} data-absent>
      {children}
    </span>
  );
}

function Fact({ name, label, children }: { name: string; label: string; children: ReactNode }) {
  return (
    <div className={own.fact} data-spend-fact={name}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** What a price basis means, in words a purchase line is read against. The two values are the
 * closed set the model enforces; anything else is printed as it arrived rather than guessed at. */
function priceBasis(set: SpendFactorSet): string {
  if (set.price_basis === "current_prices") {
    return "Current prices: a multiplier prices spend from its own year";
  }
  if (set.price_basis === "fixed_year" && set.price_basis_year !== null) {
    return `Fixed ${set.price_basis_year} prices: spend from another year must be adjusted first`;
  }
  return set.price_basis;
}

const VAT: Record<string, string> = {
  included: "Included in the denominator",
  excluded: "Excluded from the denominator",
  not_stated: "Not stated by the publisher",
};

function years(set: SpendFactorSet): string {
  if (set.first_multiplier_year === null || set.last_multiplier_year === null) return "none";
  return set.first_multiplier_year === set.last_multiplier_year
    ? String(set.first_multiplier_year)
    : `${set.first_multiplier_year} to ${set.last_multiplier_year}`;
}

function SpendSet({ set }: { set: SpendFactorSet }) {
  return (
    <section
      className={own.spendSet}
      data-testid="spend-set"
      data-set-key={set.key}
      data-jurisdiction={set.jurisdiction}
      data-locked={set.locked ? "yes" : "no"}
      data-factor-count={set.factor_count}
    >
      <h3 className={own.spendTitle}>{set.label}</h3>
      <dl className={own.facts}>
        <Fact name="publisher" label="Publisher">
          {set.publisher}
        </Fact>
        <Fact name="edition" label="Edition">
          {set.edition}
        </Fact>
        <Fact name="jurisdiction" label="Jurisdiction">
          {set.jurisdiction}
        </Fact>
        <Fact name="published_at" label="Published">
          {set.published_at ? shortDate(set.published_at) : <Absent>not stated</Absent>}
        </Fact>
        <Fact name="retrieved_at" label="Retrieved">
          {shortDate(set.retrieved_at)}
        </Fact>
        <Fact name="source" label="Source">
          {set.source_url ? (
            <a href={set.source_url} target="_blank" rel="noreferrer noopener" className={own.url}>
              {set.source_url}
            </a>
          ) : (
            <Absent>no source recorded</Absent>
          )}
        </Fact>
        <Fact name="source_sha256" label="File hash (SHA-256)">
          {set.source_sha256 ? (
            <span className={own.mono}>{set.source_sha256}</span>
          ) : (
            <Absent>no file hash recorded</Absent>
          )}
        </Fact>
        <Fact name="currency" label="Currency">
          {set.currency}
        </Fact>
        <Fact name="price_basis" label="Price basis">
          {priceBasis(set)}
        </Fact>
        <Fact name="classification" label="Classification">
          {set.classification}
        </Fact>
        <Fact name="vat_treatment" label="VAT">
          {VAT[set.vat_treatment] ?? set.vat_treatment}
        </Fact>
        <Fact name="multipliers" label="Multipliers held">
          {quantity(String(set.factor_count), 0)} for {years(set)}
        </Fact>
        <Fact name="gwp_horizon" label="GWP horizon">
          {set.gwp_horizon_years !== null ? (
            `${set.gwp_horizon_years} years, as the publisher states`
          ) : (
            <Absent>not stated by the publisher</Absent>
          )}
        </Fact>
        <Fact name="verification" label="Verified as">
          <span className={own.mono}>
            {set.verification_row}; GWP wording {set.gwp_verification_row}
          </span>
        </Fact>
        <Fact name="licence" label="Licence">
          {set.licence ?? <Absent>none recorded</Absent>}
        </Fact>
        <Fact name="locked" label="Locked">
          {set.locked ? (
            <StatusPill tone="ok">{set.locked_at ? shortDate(set.locked_at) : "yes"}</StatusPill>
          ) : (
            <StatusPill tone="crit">NOT LOCKED</StatusPill>
          )}
        </Fact>
      </dl>

      <p className={own.attribution} data-testid="spend-set-gwp">
        <strong>The publisher on CO2e:</strong> {set.gwp_statement}
      </p>
      <p className={own.attribution} data-testid="spend-set-caveat">
        <strong>The publisher&rsquo;s caveat on this use:</strong> {set.caveat}
      </p>
      {set.attribution_statement ? (
        <p className={own.attribution} data-testid="spend-set-attribution">
          {set.attribution_statement}
        </p>
      ) : (
        <p className={own.attribution} data-testid="spend-set-attribution-absent">
          No attribution statement is recorded for this set.
        </p>
      )}
    </section>
  );
}

export function FactorLibrarySpendSets() {
  const read = useSpendFactorSets();
  return (
    <Panel title="Spend-based multiplier sets" sub="Provenance only, for purchases" column="1 / 13">
      <div data-testid="spend-sets">
        {read.error ? (
          <ReadFailed
            of="The spend-based multiplier sets"
            error={read.error}
            onRetry={() => void read.refetch()}
          />
        ) : read.isPending || read.data === undefined ? (
          <Empty>Loading…</Empty>
        ) : read.data.sets.length === 0 ? (
          <p className={styles.note} data-testid="spend-sets-empty">
            No spend-based multiplier set is loaded in this deployment, so a purchase cannot be
            priced by spend here. Loading the library is an operator step; a workspace cannot do it.
          </p>
        ) : (
          <>
            <p className={styles.note} data-testid="spend-sets-scope">
              These are the multiplier sets a purchase can be priced with. Each is shown
              as who published it and on what basis; the multipliers themselves are not listed, and
              a purchase line records which series it used and why.
            </p>
            {read.data.sets.map((set) => (
              <SpendSet key={set.key} set={set} />
            ))}
          </>
        )}
      </div>
    </Panel>
  );
}
